import type { BuilderStep } from '../../lib/types.js';
import { AddStepMenu } from './AddStepMenu.js';
import { pickerVariables, type StepPath } from './definitionEdit.js';
import { StepArm, type StepArmProps } from './StepArm.js';
import { StepRow } from './StepRow.js';
import { StepEditor } from './steps/StepEditor.js';
import { type StepListContext } from './stepTypes.js';

const key = (p: StepPath): string => p.join('.');

const Arm = (props: StepArmProps) => (
  <StepArm {...props}>
    <Steps steps={props.steps} containerPath={props.containerPath} ctx={props.ctx} />
  </StepArm>
);

const Steps = ({
  steps,
  containerPath,
  ctx,
}: {
  steps: BuilderStep[];
  containerPath: StepPath;
  ctx: StepListContext;
}) => (
  <div className="grid min-w-0 gap-2">
    {steps.map((step, i) => {
      const path = [...containerPath, i];
      const k = key(path);
      const expanded = ctx.expandedPath === k;
      // A fork's arms collapse with it — but stay open while anything INSIDE
      // an arm is being edited (expanding a nested step moves expandedPath to
      // a descendant; hiding the parent would unmount the open editor).
      const armsOpen = expanded || (ctx.expandedPath?.startsWith(`${k}.`) ?? false);
      const vars = pickerVariables(ctx.definition, path, ctx.triggerFields, ctx.personas);
      const isFork = step.type === 'branch' || step.type === 'approval';
      // A fork's drawer holds its arms, so it stays open while anything
      // inside an arm is the active editor (hiding it would unmount the open
      // editor); leaf drawers open only for themselves.
      const drawerOpen = isFork ? armsOpen : expanded;
      return (
        <StepRow
          key={k}
          step={step}
          path={path}
          ctx={ctx}
          index={i}
          isLast={i === steps.length - 1}
          drawerOpen={drawerOpen}
          vars={vars}
        >
          {drawerOpen && <StepEditor step={step} path={path} ctx={ctx} Arm={Arm} />}
        </StepRow>
      );
    })}
  </div>
);

export const StepList = (ctx: StepListContext) => (
  <div className="grid min-w-0 gap-2">
    <Steps steps={ctx.definition.steps} containerPath={[]} ctx={ctx} />
    {/* Between the last step and Add step — where the next one is going. */}
    {ctx.pending}
    <AddStepMenu containerPath={[]} ctx={ctx} />
  </div>
);
